import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { API_URL } from '@core/config';
import { PlatformService } from '@core/services/platform.service';
import { SecurityService } from '@mynexus/mynexus-ui-lib';
import { AuthorizationCaseRule, RulesReferences } from '@products/dmepos/models/authorization-case-rule.model';
import { RoutingUser, ServiceLineRule, ServiceLineRulesReferences } from '@shared/models/service-line-rule.model';
import buildQuery from 'odata-query';
import { LazyLoadEvent } from 'primeng/api';
import { Observable } from 'rxjs';
import { ProductName } from '@core/constants';

export interface UserWarning {
  error: boolean;
}

@Injectable()
export abstract class RulesBaseService {
  protected readonly API_URL = inject(API_URL);

  protected readonly securityService = inject(SecurityService);

  protected readonly productName = inject(PlatformService).currentProduct;

  protected readonly http = inject(HttpClient);

  abstract readonly uniqueKey: string;

  abstract query(rawQuery: LazyLoadEvent): Observable<any>;

  abstract add(payload: ServiceLineRule): any;

  abstract update(id: string, payload: ServiceLineRule): Observable<ServiceLineRule>;

  abstract delete(id: string): Observable<null>;

  abstract getReferences(): Observable<RulesReferences | ServiceLineRulesReferences>;

  abstract getUsers(): Observable<RoutingUser[]>;

  abstract createForm(): FormGroup;

  abstract getUserWarnings(rule: ServiceLineRule | AuthorizationCaseRule): { error: boolean; message: string };

  protected readonly options = {
    headers: new HttpHeaders({ 'content-type': 'application/json' }),
  };

  searchByNpi(npi: string, tenants: string[]) {
    return this.http.get(`${this.API_URL}${this.productName.value}/providers/npis`, {
      params: {
        startsWith: npi,
        clients: tenants,
      },
    });
  }

  /**
   * Returns access permissions for the current product
   * Note: to make it more configurable it probably should be moved to the product config
   */
  getTableAccess() {
    const productName = this.productName.value;
    const product = productName?.replace('-', '').toUpperCase();

    // previous logic
    // const name = productName === 'dmepos' ? 'AuthorizationCaseRule' : 'ServiceLineRule';
    // const accessKey = `${product}${name}`;

    let accessKey = '';

    if (productName?.includes('dmepos')) {
      accessKey = productName === ProductName.DMEPOS_MA ? 'DMEPOSAuthorizationCaseRule' : 'DMEPOSMedicaidRule';
    } else {
      accessKey = `${product}ServiceLineRule`;
    }

    return {
      create: this.securityService.hasAccess([`${accessKey}.Create`]),
      delete: this.securityService.hasAccess([`${accessKey}.Delete`]),
      update: this.securityService.hasAccess([`${accessKey}.Update`]),
    };
  }

  protected buildQuery(rawQuery: LazyLoadEvent) {
    let orderBy = '';

    if (rawQuery.sortOrder && rawQuery.sortField) {
      /* eslint-disable no-magic-numbers */
      const order = rawQuery.sortOrder > 0 ? 'asc' : 'desc';
      orderBy = `${rawQuery.sortField} ${order}`;
    }

    const baseQuery = {
      count: true,
      top: rawQuery.rows,
      skip: rawQuery.first,
      orderBy,
    };

    if (!rawQuery.filters) {
      return buildQuery({ ...baseQuery });
    }

    const filter = Object.entries(rawQuery.filters).reduce((acc, [key, value]) => {
      const matchMode = value.matchMode as string;
      const collection = value.value;

      return [...acc, this.rawQueryToOdataObject(key, collection, matchMode)];
    }, [] as any);

    return buildQuery({ ...baseQuery, filter }).replaceAll(`'null'`, null as any);
  }

  /* eslint-disable unused-imports/no-unused-vars */
  protected rawQueryToOdataObject = (key: string, value: string[], matchMode: string) => {
    /* eslint-disable no-unused-vars */
    return {};
  };
}
